/**
 * THE ALLOWLIST (C60, R48; analytics spec, *The events* and *The data-layer contract*): the only events the web app
 * ever sends and the only keys an entry of the data layer may carry. No event carries a weight, a repetition, a health
 * value, text, a name or an email; the one number is the count of sets of a logged workout.
 *
 *   | Event                 | Parameters (nothing else)                                                     |
 *   |-----------------------|-------------------------------------------------------------------------------|
 *   | page_view             | none of its own: sent by the loader on every route change, never by `track`   |
 *   | login, sign_up        | method: apple, google; OPTIONAL: left out when the provider cannot be known    |
 *   | first_run_step        | step: welcome, name, greeting, date_of_birth, height, gender, training_band,  |
 *   |                       |       summary, ai_consent, analytics_consent, ready (the stage, never the answer)|
 *   | first_run_complete    | none                                                                          |
 *   | workout_start         | none                                                                          |
 *   | workout_finish        | set_count: an integer, 0 to 200                                               |
 *   | first_workout_saved   | none                                                                          |
 *   | coach_message_sent    | none (never the text or its length)                                           |
 *   | coach_card_opened     | card_type: a type of contracts/coach-artifact-types.json                      |
 *   | settings_area_changed | area: a tab of views/Settings.tsx                                             |
 *   | consent_changed       | consent: ai_processing, analytics; state: granted, declined, withdrawn         |
 *   |                       | (analytics only granted: nothing is sent after a no)                          |
 *
 * `track(event, params)` is typed so that TypeScript refuses an unknown event, an unknown parameter and a value off
 * its vocabulary, and checked again at run time, because a value can come from a server answer: an entry whose name
 * is not on the list, which lacks a parameter, carries one more, or holds a value outside its vocabulary is DROPPED
 * WHOLE, never sent with the value taken out. A change to this list is a pull request that changes the call sites,
 * the container (`landingpage/gtm/container.json`, one container for both hosts, #710) and the privacy ledger
 * together.
 */
import type { PageFields } from './scrub'

export const METHODS = ['apple', 'google'] as const
export const FIRST_RUN_STEPS = [
  'welcome',
  'name',
  'greeting',
  'date_of_birth',
  'height',
  'gender',
  'training_band',
  'summary',
  'ai_consent',
  'analytics_consent',
  'ready',
] as const
/** `contracts/coach-artifact-types.json`'s `types`; `events.test.ts` holds the two equal. */
export const CARD_TYPES = ['exercise', 'muscles', 'progression', 'record', 'session'] as const
/** `TabId` of `views/Settings.tsx`; `events.test.ts` holds the two equal. */
export const SETTINGS_AREAS = ['memory', 'health', 'equipment', 'access', 'privacy', 'credentials', 'invites', 'permissions'] as const
export const CONSENT_STATES = ['granted', 'declined', 'withdrawn'] as const
export const MAX_SET_COUNT = 200

type Method = (typeof METHODS)[number]
export type FirstRunStep = (typeof FIRST_RUN_STEPS)[number]
export type CardType = (typeof CARD_TYPES)[number]
export type SettingsArea = (typeof SETTINGS_AREAS)[number]

/** Each event `track` takes, with its parameters, or `undefined` for an event that has none. */
export interface TrackedEvents {
  login: { method?: Method }
  sign_up: { method?: Method }
  first_run_step: { step: FirstRunStep }
  first_run_complete: undefined
  workout_start: undefined
  workout_finish: { set_count: number }
  first_workout_saved: undefined
  coach_message_sent: undefined
  coach_card_opened: { card_type: CardType }
  settings_area_changed: { area: SettingsArea }
  consent_changed: { consent: 'ai_processing'; state: (typeof CONSENT_STATES)[number] } | { consent: 'analytics'; state: 'granted' }
}
export type TrackedEvent = keyof TrackedEvents

/** Every event name the data layer can carry: the page view and the tracked ones. */
export type EventName = 'page_view' | TrackedEvent

/** The entry pushed before the container loads, and again whenever the signed-in state changes. */
export const CONTEXT_EVENT = 'gw_context'

/** THE KEYS OF THE DATA-LAYER CONTRACT. Nothing else, ever, in an entry this code pushes. */
export const DATA_LAYER_KEYS = [
  'event',
  'gw_page_location',
  'gw_page_referrer',
  'gw_page_title',
  'gw_user_ref',
  'method',
  'step',
  'set_count',
  'card_type',
  'area',
  'consent',
  'state',
] as const

/** A parameter value as checked: a word of a vocabulary or the set count. */
type ParamValue = string | number
export type Params = Record<string, ParamValue>

/** An event and its checked parameters, before the page fields are added. */
export interface CheckedEvent {
  event: EventName
  params: Params
}

/** An entry of the data layer, as the loader pushes it. */
export type DataLayerEntry = { event: string } & PageFields & { [param: string]: ParamValue | null }

const oneOf =
  (words: readonly string[]) =>
  (value: unknown): boolean =>
    typeof value === 'string' && words.includes(value)
const setCount = (value: unknown): boolean => Number.isInteger(value) && (value as number) >= 0 && (value as number) <= MAX_SET_COUNT

/** Each event's parameters and the check of each value. Every parameter listed is required, but those in `OPTIONAL`. */
const SCHEMA: Record<EventName, Record<string, (value: unknown) => boolean>> = {
  page_view: {},
  login: { method: oneOf(METHODS) },
  sign_up: { method: oneOf(METHODS) },
  first_run_step: { step: oneOf(FIRST_RUN_STEPS) },
  first_run_complete: {},
  workout_start: {},
  workout_finish: { set_count: setCount },
  first_workout_saved: {},
  coach_message_sent: {},
  coach_card_opened: { card_type: oneOf(CARD_TYPES) },
  settings_area_changed: { area: oneOf(SETTINGS_AREAS) },
  consent_changed: { consent: oneOf(['ai_processing', 'analytics']), state: oneOf(CONSENT_STATES) },
}

/**
 * The parameters an event may leave out (ruling R6 of run 696): `method` of `login` and `sign_up`, which the web client
 * can only know from the tab that started the sign-in, or from the account's one linked provider; an account with two,
 * or a read that failed, is reported without it. A parameter listed here is checked when given, and left out of the
 * entry when it is absent or `undefined`; any other key, and any other missing parameter, drops the event whole. The
 * set of parameters an event can carry is `SCHEMA`'s alone, so the container's variables do not change.
 */
const OPTIONAL: Partial<Record<EventName, readonly string[]>> = { login: ['method'], sign_up: ['method'] }

/** The events, in the table's order. */
export const EVENTS = Object.keys(SCHEMA) as readonly EventName[]

function isEventName(name: unknown): name is EventName {
  return typeof name === 'string' && Object.hasOwn(SCHEMA, name)
}

/**
 * An event and its parameters, checked; or null for anything the allowlist does not admit whole. The parameters come
 * back as a new object holding exactly the schema's keys, so nothing the caller's object carries besides them (an
 * extra key, a getter, a prototype) can follow it into the data layer.
 */
export function checkEvent(name: unknown, params?: unknown): CheckedEvent | null {
  if (!isEventName(name)) return null
  const schema = SCHEMA[name]
  const given: object = params ?? {}
  if (typeof given !== 'object' || given === null || Array.isArray(given)) return null
  const proto = Object.getPrototypeOf(given)
  if (proto !== Object.prototype && proto !== null) return null
  if (!Object.keys(given).every((key) => Object.hasOwn(schema, key))) return null
  const optional = OPTIONAL[name] ?? []
  const checked: Params = {}
  for (const key of Object.keys(schema)) {
    const value: unknown = (given as Record<string, unknown>)[key]
    if (value === undefined && optional.includes(key)) continue
    // A required parameter that is missing is `undefined` here, and no vocabulary admits it.
    if (!schema[key](value)) return null
    checked[key] = value as ParamValue
  }
  // Nothing is sent after a no: the analytics consent reports its own grant and nothing else (Q3).
  if (name === 'consent_changed' && checked.consent === 'analytics' && checked.state !== 'granted') return null
  return { event: name, params: checked }
}

/** The entry for a checked event on a page: the event, the three page fields, its parameters. */
export function buildEntry(checked: CheckedEvent, page: PageFields): DataLayerEntry {
  return {
    event: checked.event,
    gw_page_location: page.gw_page_location,
    gw_page_referrer: page.gw_page_referrer,
    gw_page_title: page.gw_page_title,
    ...checked.params,
  }
}

/** The context entry: the page fields and the user ref, or null for none. */
export function buildContext(page: PageFields, userRef: string | null): DataLayerEntry {
  return {
    event: CONTEXT_EVENT,
    gw_page_location: page.gw_page_location,
    gw_page_referrer: page.gw_page_referrer,
    gw_page_title: page.gw_page_title,
    gw_user_ref: userRef,
  }
}

// ── track ──────────────────────────────────────────────────────────────────────────────────────────

type Sink = (checked: CheckedEvent) => void
let sink: Sink | undefined

/** Called by the loader at boot. Until then, and on a page where analytics is off, `track` does nothing. */
export function setSink(next: Sink | undefined): void {
  sink = next
}

/** No parameters, a parameter object that may be left out (all its members optional), or one that is required. */
type ParamsOf<E extends TrackedEvent> = TrackedEvents[E] extends undefined
  ? []
  : Partial<TrackedEvents[E]> extends TrackedEvents[E]
    ? [params?: TrackedEvents[E]]
    : [params: TrackedEvents[E]]

/**
 * Report an event. Whether anything is sent is the loader's decision, made at the moment: only with a grant of the
 * current text, and on a page where the container may be (a credential route queues it for the same page life).
 * Without a grant it is dropped, never kept.
 *
 *   track('workout_finish', { set_count: 7 })
 *   track('coach_message_sent')
 *   track('login', { method: 'apple' })
 *   track('login')                       // the provider could not be known
 */
export function track<E extends TrackedEvent>(event: E, ...params: ParamsOf<E>): void {
  if (!sink) return
  const checked = checkEvent(event, params[0])
  if (checked && checked.event !== 'page_view') sink(checked)
}
